/**
 * Hauler Manager
 * Handles automated fish selling/hauling buddy logic
 */

import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { HaulerBuddy, DEFAULT_STATS, calculateXPForLevel, awardXP, FishStack, GameEvent, isBelowQualityThreshold, Quality } from '@/types';
import { BALANCE_CONFIG } from '@/data/balance/balanceConfig';
import { sellManager } from '../selling/SellManager';

export class HaulerManager {
  private updateIntervalId: number | null = null;
  private readonly UPDATE_FREQUENCY = 1000; // Check every second

  // Track active loading intervals for each hauler
  private loadingIntervals: Map<number, number> = new Map();

  constructor() {
    console.log('🚚 HaulerManager initialized');
  }

  /**
   * Start the hauler update loop
   */
  start(): void {
    if (this.updateIntervalId !== null) {
      console.warn('HaulerManager already started');
      return;
    }

    console.log('🚚 Starting hauler auto-selling');
    this.updateIntervalId = window.setInterval(() => {
      this.update();
    }, this.UPDATE_FREQUENCY);
  }

  /**
   * Stop the hauler update loop
   */
  stop(): void {
    if (this.updateIntervalId !== null) {
      window.clearInterval(this.updateIntervalId);
      this.updateIntervalId = null;
      console.log('🚚 Stopped hauler auto-selling');
    }

    // Clear all loading intervals
    this.loadingIntervals.forEach((intervalId) => {
      window.clearInterval(intervalId);
    });
    this.loadingIntervals.clear();
  }

  /**
   * Main update loop - check all haulers for state transitions
   */
  private update(): void {
    const state = useGameStore.getState();
    const haulers = state.playerState.haulerBuddies;

    if (!haulers || haulers.length === 0) {
      return; // No haulers to update
    }

    const now = Date.now();

    haulers.forEach((hauler, index) => {
      if (!hauler.hired || !hauler.enabled) {
        return; // Skip unhired or disabled haulers
      }

      // Special handling for idle state - check if we should start loading
      if (hauler.state === 'idle') {
        const inventory = state.playerState.inventory;
        const capacity = this.calculateEffectiveCapacity(hauler);

        // Only start loading if inventory has enough fish (at least capacity, or close to it)
        // This prevents haulers from making inefficient trips with just 1-2 fish
        if (inventory.currentCount >= capacity) {
          // Full load available, start loading
          this.startLoadingPhase(index);
        }
        return;
      }

      // Check if it's time for state transition
      if (hauler.stateCompletesAt && now >= hauler.stateCompletesAt) {
        this.transitionState(hauler, index);
      }
    });
  }

  /**
   * State machine transition handler
   */
  private transitionState(hauler: HaulerBuddy, index: number): void {
    switch (hauler.state) {
      case 'idle':
        this.startLoadingPhase(index);
        break;
      case 'loading':
        this.startTravelingPhase(index);
        break;
      case 'traveling':
        this.startSellingPhase(index);
        break;
      case 'selling':
        this.startReturningPhase(index);
        break;
      case 'returning':
        this.returnToIdle(index);
        break;
    }
  }

  /**
   * Start loading phase - select fish from inventory and animate loading
   */
  private startLoadingPhase(index: number): void {
    const state = useGameStore.getState();
    const hauler = state.playerState.haulerBuddies[index];

    if (!hauler) return;

    // Check if inventory has fish
    const inventory = state.playerState.inventory;
    if (inventory.currentCount === 0) {
      // No fish to haul, stay idle
      console.log(`🚚 Hauler ${hauler.slotNumber} idle - no fish in inventory`);
      return;
    }

    // Select fish for hauling (FIFO) - this determines what to load
    const fishToLoad = this.selectFishForHauler(hauler);

    if (fishToLoad.length === 0) {
      // Nothing to load, stay idle
      return;
    }

    // Calculate total fish count
    const totalFishCount = fishToLoad.reduce((sum, s) => sum + s.count, 0);

    const now = Date.now();
    const loadingTime = BALANCE_CONFIG.HAULER_LOADING_TIME * 1000;

    // Update hauler state to loading (start with empty cargo)
    useGameStore.setState((draft) => {
      const h = draft.playerState.haulerBuddies[index];
      if (h) {
        h.state = 'loading';
        h.stateStartedAt = now;
        h.stateCompletesAt = now + loadingTime;
        h.currentCargo = []; // Start empty, will increment
        h.currentCargoCount = 0; // Start at 0, will animate up
      }
    });

    console.log(`🚚 Hauler ${hauler.slotNumber} loading ${totalFishCount} fish (animated)`);

    // Start animated loading - load one fish at a time
    this.startAnimatedLoading(index, fishToLoad, loadingTime);
  }

  /**
   * Animate loading fish one at a time
   */
  private startAnimatedLoading(index: number, fishToLoad: FishStack[], loadingTime: number): void {
    const state = useGameStore.getState();
    const hauler = state.playerState.haulerBuddies[index];

    if (!hauler) return;

    // Flatten fish stacks into individual fish for animation
    const individualFish: { definitionId: string; quality: Quality }[] = [];
    fishToLoad.forEach((stack) => {
      for (let i = 0; i < stack.count; i++) {
        individualFish.push({
          definitionId: stack.definitionId,
          quality: stack.quality,
        });
      }
    });

    const totalFish = individualFish.length;
    if (totalFish === 0) return;

    // Calculate interval timing - spread fish loading over the loading time
    const msPerFish = loadingTime / totalFish;
    let fishLoaded = 0;

    // Clear any existing loading interval for this hauler
    this.clearLoadingInterval(index);

    // Start loading interval
    const intervalId = window.setInterval(() => {
      if (fishLoaded >= totalFish) {
        // All fish loaded, clear interval
        this.clearLoadingInterval(index);
        return;
      }

      const fish = individualFish[fishLoaded];

      // Remove one fish from inventory
      const currentState = useGameStore.getState();
      currentState.removeFishFromInventory(fish.definitionId, fish.quality, 1);

      // Add one fish to hauler cargo
      useGameStore.setState((draft) => {
        const h = draft.playerState.haulerBuddies[index];
        if (h) {
          // Increment count
          h.currentCargoCount++;

          // Update cargo stacks
          const existingStack = h.currentCargo.find(
            (s) => s.definitionId === fish.definitionId && s.quality === fish.quality
          );

          if (existingStack) {
            existingStack.count++;
          } else {
            h.currentCargo.push({
              definitionId: fish.definitionId,
              quality: fish.quality,
              count: 1,
            });
          }
        }
      });

      fishLoaded++;
    }, msPerFish);

    // Store the interval ID
    this.loadingIntervals.set(index, intervalId);
  }

  /**
   * Clear loading interval for a hauler
   */
  private clearLoadingInterval(haulerIndex: number): void {
    const intervalId = this.loadingIntervals.get(haulerIndex);
    if (intervalId !== undefined) {
      window.clearInterval(intervalId);
      this.loadingIntervals.delete(haulerIndex);
    }
  }

  /**
   * Start traveling phase - hauler goes to shop
   */
  private startTravelingPhase(index: number): void {
    const hauler = useGameStore.getState().playerState.haulerBuddies[index];

    // Clear any ongoing loading interval (in case loading completed)
    this.clearLoadingInterval(index);

    if (!hauler || hauler.currentCargoCount === 0) {
      // No cargo, return to idle
      this.returnToIdle(index);
      return;
    }

    const now = Date.now();
    const travelTime = this.calculateTravelTime(hauler);

    useGameStore.setState((draft) => {
      const h = draft.playerState.haulerBuddies[index];
      if (h) {
        h.state = 'traveling';
        h.stateStartedAt = now;
        h.stateCompletesAt = now + travelTime;
      }
    });

    // Emit event for UI
    eventBus.emit(GameEvent.HAULER_STARTED_TRIP, {
      haulerSlot: hauler.slotNumber,
      cargoCount: hauler.currentCargoCount,
    });

    console.log(`🚚 Hauler ${hauler.slotNumber} traveling to shop (${(travelTime / 1000).toFixed(1)}s)`);
  }

  /**
   * Start selling phase - sell fish at shop
   */
  private startSellingPhase(index: number): void {
    const hauler = useGameStore.getState().playerState.haulerBuddies[index];

    if (!hauler || hauler.currentCargo.length === 0) {
      // No cargo to sell, return to idle
      this.returnToIdle(index);
      return;
    }

    const now = Date.now();
    const sellingTime = BALANCE_CONFIG.HAULER_SELLING_TIME * 1000;

    // Sell fish via SellManager
    const transaction = sellManager.sellFish(hauler.currentCargo, 'camp_chef');

    // Award XP based on gold earned (1 XP per 10g)
    const xpGained = Math.floor(transaction.finalValue * BALANCE_CONFIG.HAULER_XP_PER_GOLD);
    this.awardHaulerXP(index, xpGained);

    // Update stats
    this.updateHaulerStats(index, hauler.currentCargoCount, transaction.finalValue);

    // Emit event
    eventBus.emit(GameEvent.HAULER_SOLD_FISH, {
      haulerSlot: hauler.slotNumber,
      fishCount: hauler.currentCargoCount,
      value: transaction.finalValue,
    });

    console.log(`🚚 Hauler ${hauler.slotNumber} sold ${hauler.currentCargoCount} fish for ${transaction.finalValue.toFixed(0)}g (${xpGained} XP)`);

    // Update state
    useGameStore.setState((draft) => {
      const h = draft.playerState.haulerBuddies[index];
      if (h) {
        h.state = 'selling';
        h.stateStartedAt = now;
        h.stateCompletesAt = now + sellingTime;
        // Keep cargo for now (will clear in returning phase)
      }
    });
  }

  /**
   * Start returning phase - hauler travels back
   */
  private startReturningPhase(index: number): void {
    const hauler = useGameStore.getState().playerState.haulerBuddies[index];

    if (!hauler) return;

    const now = Date.now();
    // Return trip is half the travel time (one-way)
    const returnTime = this.calculateTravelTime(hauler) / 2;

    useGameStore.setState((draft) => {
      const h = draft.playerState.haulerBuddies[index];
      if (h) {
        h.state = 'returning';
        h.stateStartedAt = now;
        h.stateCompletesAt = now + returnTime;
        // Clear cargo
        h.currentCargo = [];
        h.currentCargoCount = 0;
      }
    });

    console.log(`🚚 Hauler ${hauler.slotNumber} returning (${(returnTime / 1000).toFixed(1)}s)`);
  }

  /**
   * Return to idle state and immediately check for next trip
   */
  private returnToIdle(index: number): void {
    const hauler = useGameStore.getState().playerState.haulerBuddies[index];

    if (!hauler) return;

    useGameStore.setState((draft) => {
      const h = draft.playerState.haulerBuddies[index];
      if (h) {
        h.state = 'idle';
        h.stateStartedAt = null;
        h.stateCompletesAt = null;
      }
    });

    // Emit return event
    eventBus.emit(GameEvent.HAULER_RETURNED, {
      haulerSlot: hauler.slotNumber,
    });

    console.log(`🚚 Hauler ${hauler.slotNumber} returned to idle`);

    // Note: Update loop will check for next trip on the next tick
  }

  /**
   * Select fish for hauler (FIFO - oldest first, excluding protected fish)
   */
  private selectFishForHauler(hauler: HaulerBuddy): FishStack[] {
    const state = useGameStore.getState();
    const inventory = state.playerState.inventory;
    const autoSellSettings = state.playerState.autoSellSettings;
    const protectedFish = autoSellSettings.protectedFish;
    const threshold = autoSellSettings.qualityThreshold;
    const capacity = this.calculateEffectiveCapacity(hauler);

    const selectedFish: FishStack[] = [];
    let remainingCapacity = capacity;

    // FIFO: Take from inventory.stacks in order (oldest first), skip protected fish
    for (const stack of inventory.stacks) {
      if (remainingCapacity === 0) break;

      // Skip explicitly protected fish (per-fish lock takes priority)
      const isProtected = protectedFish[stack.definitionId]?.[stack.quality];
      if (isProtected) {
        console.log(`🔒 Skipping protected fish: ${stack.definitionId} (${stack.quality})`);
        continue;
      }

      // Skip fish at or above quality threshold (player wants to keep these)
      if (threshold && !isBelowQualityThreshold(stack.quality, threshold)) {
        continue;
      }

      const takeCount = Math.min(stack.count, remainingCapacity);
      selectedFish.push({
        definitionId: stack.definitionId,
        quality: stack.quality,
        count: takeCount,
      });
      remainingCapacity -= takeCount;
    }

    return selectedFish;
  }

  /**
   * Calculate hauler's effective carry capacity
   */
  calculateEffectiveCapacity(hauler: HaulerBuddy): number {
    const baseCapacity = BALANCE_CONFIG.HAULER_BASE_CAPACITY;
    const physiqueBonus = Math.floor(
      hauler.progression.stats.physique * BALANCE_CONFIG.HAULER_CAPACITY_PER_PHYSIQUE
    );
    return baseCapacity + physiqueBonus;
  }

  /**
   * Calculate hauler's travel time (round trip)
   */
  calculateTravelTime(hauler: HaulerBuddy): number {
    const baseTime = BALANCE_CONFIG.HAULER_BASE_TRAVEL_TIME;
    const speedBonus = hauler.progression.stats.eyesight * BALANCE_CONFIG.HAULER_SPEED_PER_EYESIGHT;
    // Speed bonus reduces travel time
    return (baseTime / (1 + speedBonus)) * 1000; // Convert to ms
  }

  /**
   * Update hauler statistics
   */
  private updateHaulerStats(haulerIndex: number, fishSold: number, valueGenerated: number): void {
    useGameStore.setState((draft) => {
      const hauler = draft.playerState.haulerBuddies[haulerIndex];
      if (hauler) {
        hauler.totalTripsCompleted++;
        hauler.totalFishSold += fishSold;
        hauler.totalValueGenerated += valueGenerated;
      }
    });
  }

  /**
   * Award XP to a hauler
   */
  private awardHaulerXP(haulerIndex: number, xpAmount: number): void {
    if (xpAmount <= 0) return;

    useGameStore.setState((draft) => {
      const hauler = draft.playerState.haulerBuddies[haulerIndex];
      if (hauler) {
        const oldLevel = hauler.progression.progress.level;
        const newProgression = awardXP(hauler.progression, xpAmount);
        hauler.progression = newProgression;

        // Log level-up if it occurred
        if (newProgression.progress.level > oldLevel) {
          const haulerName = hauler.name || `Hauler ${hauler.slotNumber}`;
          console.log(
            `🎉 LEVEL UP! ${haulerName} reached level ${newProgression.progress.level}! ` +
            `Stat points available: ${newProgression.progress.unspentStatPoints}`
          );
          eventBus.emit(GameEvent.BUDDY_LEVEL_UP, {
            buddyName: haulerName,
            buddyType: 'hauler',
            newLevel: newProgression.progress.level,
            oldLevel,
          });
        }
      }
    });
  }

  /**
   * Hire a new hauler (unlock slot)
   */
  hireHauler(slotNumber: number): boolean {
    const state = useGameStore.getState();

    // Validate slot number (only 2 hauler slots)
    if (slotNumber < 1 || slotNumber > 2) {
      console.error(`Invalid hauler slot: ${slotNumber}`);
      return false;
    }

    // Check if already hired
    const existingHauler = state.playerState.haulerBuddies.find(
      (h) => h.slotNumber === slotNumber
    );
    if (existingHauler?.hired) {
      console.warn(`Hauler ${slotNumber} already hired`);
      return false;
    }

    // Get cost for this slot
    const cost = this.getHaulerCost(slotNumber);

    // Check if player can afford
    if (state.playerState.currency < cost) {
      console.warn(`Not enough currency to hire hauler ${slotNumber}`);
      return false;
    }

    // Deduct cost
    state.addCurrency(-cost);

    // Create and add the hauler
    const newHauler: HaulerBuddy = {
      slotNumber,
      hired: true,
      enabled: true,
      state: 'idle',
      stateStartedAt: null,
      stateCompletesAt: null,
      currentCargo: [],
      currentCargoCount: 0,
      totalTripsCompleted: 0,
      totalFishSold: 0,
      totalValueGenerated: 0,
      progression: {
        stats: DEFAULT_STATS,
        progress: {
          level: 1,
          currentXP: 0,
          xpToNextLevel: calculateXPForLevel(2),
          unspentStatPoints: 0,
        },
      },
    };

    useGameStore.setState((draft) => {
      draft.playerState.haulerBuddies.push(newHauler);
    });

    eventBus.emit(GameEvent.HAULER_HIRED, { slotNumber, cost });
    console.log(`🚚 Hired hauler ${slotNumber} for ${cost} gold`);

    return true;
  }

  /**
   * Toggle hauler enabled/disabled
   */
  toggleHauler(slotNumber: number): void {
    const state = useGameStore.getState();
    const haulerIndex = state.playerState.haulerBuddies.findIndex((h) => h.slotNumber === slotNumber);

    if (haulerIndex === -1) return;

    useGameStore.setState((draft) => {
      const hauler = draft.playerState.haulerBuddies[haulerIndex];
      if (hauler) {
        hauler.enabled = !hauler.enabled;
        console.log(`🚚 Hauler ${slotNumber} ${hauler.enabled ? 'enabled' : 'disabled'}`);

        // If disabled, reset to idle and clear loading interval
        if (!hauler.enabled) {
          hauler.state = 'idle';
          hauler.stateStartedAt = null;
          hauler.stateCompletesAt = null;
          this.clearLoadingInterval(haulerIndex);
        }
      }
    });
  }

  /**
   * Rename a hauler
   */
  renameHauler(slotNumber: number, newName: string): void {
    useGameStore.setState((draft) => {
      const hauler = draft.playerState.haulerBuddies.find((h) => h.slotNumber === slotNumber);
      if (hauler) {
        hauler.name = newName.trim() || undefined;
        console.log(`🚚 Hauler ${slotNumber} renamed to: ${hauler.name || 'Hauler ' + slotNumber}`);
      }
    });
  }

  /**
   * Force hauler to start hauling immediately (bypasses capacity check)
   */
  forceHaul(slotNumber: number): void {
    const state = useGameStore.getState();
    const haulerIndex = state.playerState.haulerBuddies.findIndex((h) => h.slotNumber === slotNumber);

    if (haulerIndex === -1) {
      console.warn(`Hauler ${slotNumber} not found`);
      return;
    }

    const hauler = state.playerState.haulerBuddies[haulerIndex];

    if (!hauler.hired || !hauler.enabled) {
      console.warn(`Hauler ${slotNumber} is not hired or is disabled`);
      return;
    }

    if (hauler.state !== 'idle') {
      console.warn(`Hauler ${slotNumber} is already working (state: ${hauler.state})`);
      return;
    }

    const inventory = state.playerState.inventory;
    if (inventory.currentCount === 0) {
      console.warn(`Cannot force haul - inventory is empty`);
      return;
    }

    console.log(`🚚 [FORCE HAUL] Hauler ${slotNumber} starting immediate haul with ${inventory.currentCount} fish`);
    this.startLoadingPhase(haulerIndex);
  }

  /**
   * Get cost for a hauler slot
   */
  getHaulerCost(slotNumber: number): number {
    if (slotNumber < 1 || slotNumber > BALANCE_CONFIG.HAULER_BUDDY_COSTS.length) {
      return Infinity;
    }
    return BALANCE_CONFIG.HAULER_BUDDY_COSTS[slotNumber - 1];
  }

  /**
   * Check if player can afford a hauler slot
   */
  canAffordHauler(slotNumber: number): boolean {
    const state = useGameStore.getState();
    const cost = this.getHaulerCost(slotNumber);
    return state.playerState.currency >= cost;
  }

  /**
   * Get hauler by slot number
   */
  getHauler(slotNumber: number): HaulerBuddy | undefined {
    const state = useGameStore.getState();
    return state.playerState.haulerBuddies.find((h) => h.slotNumber === slotNumber);
  }

  /**
   * Get all hired haulers
   */
  getHiredHaulers(): HaulerBuddy[] {
    const state = useGameStore.getState();
    return state.playerState.haulerBuddies.filter((h) => h.hired);
  }
}

// Singleton instance
export const haulerManager = new HaulerManager();
